import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { DataTableNumberColumn } from "~components/DataTable/components/DataTableNumberColumn/DataTableNumberColumn.tsx";
import { DataTableStringColumn } from "~components/DataTable/components/DataTableStringColumn/DataTableStringColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import type { InfinitySelectOption } from "~components/InfinitySelect/InfinitySelect.tsx";
import { SelectStage } from "~components/SelectStage/SelectStage.tsx";
import { useSeasonPoolService } from "~pages/SeasonPools/hooks/useSeasonPoolService.ts";
import { SeasonPoolTableColumns } from "~pages/SeasonPools/SeasonPoolListPage/enums/seasonPoolTableColumns.ts";
import type { UpdateSeasonPoolModel } from "~pages/SeasonPools/types/seasonPoolModel.ts";
import type { SeasonPoolTransformed } from "~pages/SeasonPools/types/seasonPoolTransformed.ts";

export const useGetSeasonPoolTableColumns = (massEditMode: boolean): Array<DataTableColumn<SeasonPoolTransformed>> => {
  const { formatMessage } = useIntl();
  const { updateSeasonPool } = useSeasonPoolService();

  const updateSeasonPoolField = async (row: SeasonPoolTransformed & DataTableRow, updateData: Omit<UpdateSeasonPoolModel, "poolCode">) => {
    row.disabled = true;
    try {
      await updateSeasonPool(row.poolCode, updateData);
    } finally {
      row.disabled = false;
    }
  };

  return [
    {
      key: SeasonPoolTableColumns.NameLocal,
      title: formatMessage({ id: "name.local" }),
      dataIndex: "nameLocal",
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          required
          onSave={(nameLocal) => updateSeasonPoolField(row, { nameLocal })}
        />
      ),
    },
    {
      key: SeasonPoolTableColumns.AbbreviationLocal,
      title: formatMessage({ id: "name.local.abbreviation" }),
      dataIndex: "abbreviationLocal",
      width: 100,
      sorter: true,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLocal) => updateSeasonPoolField(row, { abbreviationLocal: abbreviationLocal || null })}
        />
      ),
    },
    {
      key: SeasonPoolTableColumns.AbbreviationLatin,
      title: formatMessage({ id: "name.latin.abbreviation" }),
      dataIndex: "abbreviationLatin",
      width: 100,
      sorter: true,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLatin) => updateSeasonPoolField(row, { abbreviationLatin: abbreviationLatin || null })}
        />
      ),
    },
    {
      key: SeasonPoolTableColumns.PoolCode,
      title: formatMessage({ id: "pool.code" }),
      dataIndex: "poolCode",
      copyable: true,
      width: 240,
      ellipsis: { showTitle: true },
      sorter: true,
    },
    {
      key: SeasonPoolTableColumns.PoolOrder,
      title: formatMessage({ id: "pool.order" }),
      dataIndex: "poolOrder",
      width: 150,
      sorter: true,
      render: (value: number | null, row) => (
        <DataTableNumberColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          required
          onSave={(poolOrder) => updateSeasonPoolField(row, { poolOrder })}
        />
      ),
    },
    {
      key: SeasonPoolTableColumns.StageName,
      title: formatMessage({ id: "stage" }),
      dataIndex: "stageName",
      width: 200,
      ellipsis: { showTitle: true },
      render: (value: string | null, row) => {
        if (massEditMode) {
          const selectValue: InfinitySelectOption | null = isEmpty(row.stageCode)
            ? null
            : {
                label: value || row.stage?.nameLocal || row.stageCode || "",
                value: row.stageCode || "",
              };

          return (
            <SelectStage
              disabled={row.disabled}
              value={selectValue}
              onChange={(stageOption) => void updateSeasonPoolField(row, { stageCode: stageOption?.value ? stageOption.value : null })}
            />
          );
        }

        return value ?? "";
      },
    },
    {
      key: SeasonPoolTableColumns.NameLatin,
      title: formatMessage({ id: "name.latin" }),
      dataIndex: "nameLatin",
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(nameLatin) => updateSeasonPoolField(row, { nameLatin: nameLatin || null })}
        />
      ),
    },
    {
      key: SeasonPoolTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: "externalId",
      copyable: true,
      width: 200,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(externalId) => updateSeasonPoolField(row, { externalId: externalId || null })}
        />
      ),
    },
  ];
};
